import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CardFooter } from 'reactstrap';
import { When, Switch, Case, If, Then, Else } from 'react-if';
import * as QUESTION from '../../../../constants/questions';
import YesNo from './YesNo';
import Multiple from './Multiple';
import Open from './Open';
import './styles.css';
import { useToggle } from 'react-use';

import PostOwnerFooter from './PostOwnerFooter';
import Review from './Review';
import { Col, Container, Row } from 'react-bootstrap';
import YesNoStatistic from './YesNoStatistic';
import MultipleStatistic from './MultipleStatistic';
import { Categories, DefaultCategory } from '../../../../static/categories';
import { useAuth } from '../../../../hooks/useAuth';

const YES_BUTTON_TEXT = 'YES';

const PostFooter = ({
    postId,
    questionType,
    questions,
    reviews: rawReviews,
    count,
    yesCount,
    categoryId,
    alreadyReviewed,
    needShowLoadMoreButton,
    isPostOwner,
    measure,
    onClick,
    onRefresh,
    onReviewsOpen,
    onEmailAllButtonClick,
    onMultipleQuestionClick,
    onInviteFriendsButtonClick,
    onLoadMoreReviewsButtonClick,
}) => {
    const [areReviewsOpen, toggleReviewsOpenStatus] = useToggle(false);
    const { currentUser } = useAuth();

    const postCategory = useMemo(() => {
        return Categories[categoryId] ? Categories[categoryId] : DefaultCategory;
    }, [categoryId]);

    const reviews = useMemo(() => {
        return rawReviews.filter(r => (isPostOwner ? true : r.publisherUid === currentUser.uid));
    }, [currentUser.uid, isPostOwner, rawReviews]);

    const handleOpenReviewsButtonClick = useCallback(() => {
        toggleReviewsOpenStatus();
        onReviewsOpen();
    }, [toggleReviewsOpenStatus, onReviewsOpen]);

    const handlePaySuccess = useCallback(() => {
        onRefresh();
    }, [onRefresh]);

    return (
        <CardFooter>
            <If condition={isPostOwner}>
                <Then>
                    <PostOwnerFooter
                        reviews={reviews}
                        categoryId={categoryId}
                        count={count}
                        yesCount={yesCount}
                        questions={questions}
                        questionType={questionType}
                        postCategory={postCategory}
                        onReviewsOpen={handleOpenReviewsButtonClick}
                        onEmailAllButtonClick={onEmailAllButtonClick}
                        onInviteFriendsButtonClick={onInviteFriendsButtonClick}
                    />
                </Then>
                <Else>
                    <div className="post-footer__container">
                        <If condition={!!alreadyReviewed}>
                            <Then>
                                <div className="post-footer__row d-flex align-items-center justify-content-center">
                                    <Container>
                                        <Row>
                                            <Col lg={4} />
                                            <Col
                                                lg={4}
                                                className="align-items-center d-flex justify-content-center m-0"
                                            >
                                                {/* <If condition={areReviewsOpen}>
                                                    <Then>
                                                        <span className="d-flex align-items-center post__comments-text">
                                                            <b className="mr-1">Close</b>{' '}
                                                            {reviews.length} Comment
                                                            <When condition={reviews.length >= 2}>
                                                                s
                                                            </When>
                                                            <i className="material-icons">
                                                                keyboard_arrow_up
                                                            </i>
                                                        </span>
                                                    </Then>
                                                    <Else>
                                                        <span className="d-flex align-items-center post__comments-text">
                                                            <b className="mr-1">Open</b>{' '}
                                                            {reviews.length} Comment
                                                            <When condition={reviews.length >= 2}>
                                                                s
                                                            </When>
                                                            <i className="material-icons">
                                                                keyboard_arrow_down
                                                            </i>
                                                        </span>
                                                    </Else>
                                                </If> */}
                                            </Col>
                                            <Col
                                                lg={4}
                                                className="align-items-center d-flex justify-content-end"
                                            >
                                                <Switch>
                                                    <Case
                                                        condition={questionType === QUESTION.YES_NO}
                                                    >
                                                        <YesNoStatistic
                                                            questions={questions}
                                                            color={postCategory.color}
                                                        />
                                                    </Case>
                                                    <Case
                                                        condition={
                                                            questionType === QUESTION.MULTIPLE
                                                        }
                                                    >
                                                        <MultipleStatistic
                                                            questions={questions}
                                                            color={postCategory.color}
                                                        />
                                                    </Case>
                                                </Switch>
                                            </Col>
                                        </Row>
                                    </Container>
                                </div>
                            </Then>
                            <Else>
                                <div className="post-footer__row d-flex align-items-center justify-content-end">
                                    <div className="flex" />
                                    <div className="post-footer__text">
                                        <Switch>
                                            <Case condition={questionType === QUESTION.YES_NO}>
                                                Are you available for this project?
                                            </Case>
                                            <Case condition={questionType === QUESTION.OPEN}>
                                                Can you offer advice? Great advice is worthy of a
                                                donation.
                                            </Case>
                                            <Case condition={questionType === QUESTION.MULTIPLE}>
                                                Select your skill set from the list above.
                                            </Case>
                                        </Switch>
                                    </div>
                                    <div className="d-flex flex justify-content-end">
                                        <Switch>
                                            <Case condition={questionType === QUESTION.YES_NO}>
                                                <YesNo questions={questions} onClick={onClick} />
                                            </Case>
                                            <Case condition={questionType === QUESTION.MULTIPLE}>
                                                <Multiple onClick={onMultipleQuestionClick} />
                                            </Case>
                                            <Case condition={questionType === QUESTION.OPEN}>
                                                <Open questions={questions} onClick={onClick} />
                                            </Case>
                                        </Switch>
                                    </div>
                                </div>
                            </Else>
                        </If>
                    </div>
                </Else>
            </If>
            <When condition={areReviewsOpen}>
                <div>
                    {reviews.map(r => (
                        <If
                            condition={
                                (questionType === QUESTION.YES_NO &&
                                    r.questions[0] &&
                                    r.questions[0].title === YES_BUTTON_TEXT) ||
                                questionType !== QUESTION.YES_NO
                            }
                            key={r.uid}
                        >
                            <Review
                                id={r.uid}
                                postType={questionType}
                                review={r}
                                isPostOwner={isPostOwner}
                                alreadyPaid={r.alreadyPaid}
                                postId={postId}
                                onRemeasure={onReviewsOpen}
                                onMeasure={measure}
                                onPaySuccess={handlePaySuccess}
                            />
                        </If>
                    ))}
                </div>
                <When condition={needShowLoadMoreButton}>
                    {() => (
                        <div className="reviews__pagination d-flex justify-content-center align-items-center">
                            <span
                                className="reviews__pagination-load-more"
                                onClick={onLoadMoreReviewsButtonClick}
                            >
                                Load more
                            </span>
                        </div>
                    )}
                </When>
            </When>
        </CardFooter>
    );
};

PostFooter.propTypes = {
    postId: PropTypes.string.isRequired,
    reviews: PropTypes.array.isRequired,
    count: PropTypes.number.isRequired,
    categoryId: PropTypes.string.isRequired,
    questionType: PropTypes.number.isRequired,
    questions: PropTypes.array.isRequired,
    alreadyReviewed: PropTypes.bool.isRequired,
    isPostOwner: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
    needShowLoadMoreButton: PropTypes.bool.isRequired,
    onEmailAllButtonClick: PropTypes.func.isRequired,
    onMultipleQuestionClick: PropTypes.func.isRequired,
    onInviteFriendsButtonClick: PropTypes.func.isRequired,
    onReviewsOpen: PropTypes.func.isRequired,
    onRefresh: PropTypes.func.isRequired,
    onLoadMoreReviewsButtonClick: PropTypes.func.isRequired,
};

export default React.memo(PostFooter);
